// src/components/bucket/Accommodation/AddAccommodation.jsx
import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
  SimpleGrid,
  GridItem,
  Textarea,
} from "@chakra-ui/react";
import currencyFormatter from "currency-formatter";
import axiosInstance from "../../../helpers/axiosInstance";
import Translate from "../../../components/LanguageTranslation/Translate";

// Helper to capitalize first letter of each word
const capitalizeWords = (str) =>
  str.replace(/\b\w/g, (char) => char.toUpperCase());

const AddAccommodation = ({
  isOpen,
  onClose,
  addTripItem,
  defaultSubcategories = [],
  editItem = null,
  onEditSuccess = null,
}) => {
  // common fields
  const [title, setTitle] = useState("");
  const [link, setLink] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [errors, setErrors] = useState({});
  const availableCurrencies = currencyFormatter.currencies.map((c) => ({
    code: c.code,
    label: `${c.code} ${c.symbol || ""}`,
  }));

  // Reset form whenever modal opens
  useEffect(() => {
    if (isOpen) {
      if (editItem) {
        setTitle(editItem.title || "");
        setLink(editItem.link || "");
        setPrice(editItem.price?.perNight?.toString() || "");
        setCurrency(editItem.price?.currency || "USD");
      } else {
        setTitle("");
        setLink("");
        setPrice("");
        setCurrency("USD");
      }
      setErrors({});
    }
  }, [isOpen, editItem]);

  const normalizedURL = (url) =>
    url.startsWith("http") ? url : `https://${url}`;
  const isValidURL = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleCreate = async () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = "Title is required";
    if (!link.trim() || !isValidURL(normalizedURL(link)))
      newErrors.link = "Valid URL required";
    if (!price || isNaN(price) || price <= 0)
      newErrors.price = "Valid price required";

    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;

    // Only normal 3rd-party accommodation payload
    const payload = {
      title,
      link: normalizedURL(link),
      price_per_night: parseFloat(price),
      currency,
      preference: defaultSubcategories[0],
    };

    // Add item_id to payload if editing
    if (editItem && editItem._id) {
      payload.item_id = editItem._id;
    }

    try {
      const res = await axiosInstance.post(
        "/bucket-list/add-manual-accommodation",
        payload
      );
      if (res.data.success) {
        let priceObj = { perNight: parseFloat(price), currency };

        if (editItem && editItem._id) {
          // Update existing item
          const updatedItem = {
            ...editItem,
            title,
            link: normalizedURL(link),
            price: priceObj,
          };
          if (onEditSuccess) {
            onEditSuccess(updatedItem);
          }
        } else {
          // Add new item
          const newItem = {
            _id: res.data.data._id,
            data: {
              title,
              link: normalizedURL(link),
              price: priceObj,
            },
            category: "accommodation",
            preference: defaultSubcategories[0],
            data_saved: "manual",
          };

          addTripItem(newItem);
        }
      }
    } catch (err) {
      console.error(err);
    }

    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent maxH="80vh" overflowY="auto">
        <ModalHeader>
          <Translate
            text={
              editItem
                ? "Edit Accommodation"
                : `Add ${
                    defaultSubcategories[0]
                      ? capitalizeWords(defaultSubcategories[0])
                      : "Accommodation"
                  }`
            }
          />
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleGrid columns={2} spacing={4}>
            {/* Title */}
            <GridItem colSpan={2}>
              <FormControl isInvalid={errors.title}>
                <FormLabel>
                  {" "}
                  <Translate text="Title" />
                </FormLabel>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <FormErrorMessage>
                  {" "}
                  <Translate text={errors.title} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>

            {/* Website */}
            <GridItem colSpan={2}>
              <FormControl isInvalid={errors.link}>
                <FormLabel>
                  {" "}
                  <Translate text="Booking Link" />
                </FormLabel>
                <Input value={link} onChange={(e) => setLink(e.target.value)} />
                <FormErrorMessage>
                  {" "}
                  <Translate text={errors.link} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>

            {/* Price */}
            <GridItem colSpan={1}>
              <FormControl isInvalid={errors.price}>
                <FormLabel>
                  {" "}
                  <Translate text="Price" /> Per Night
                </FormLabel>
                <Input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                />
                <FormErrorMessage>
                  {" "}
                  <Translate text={errors.price} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>

            {/* Currency */}
            <GridItem colSpan={1}>
              <FormControl>
                <FormLabel>
                  {" "}
                  <Translate text="Currency" />
                </FormLabel>
                <Select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  {availableCurrencies.map((c) => (
                    <option key={c.code} value={c.code}>
                      <Translate text={c.label} />
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="customGreen" mr={3} onClick={handleCreate}>
            <Translate text="Save" />
          </Button>
          <Button onClick={onClose}>
            {" "}
            <Translate text="Cancel" />
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddAccommodation;
